import type { IntlService } from 'ember-intl';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type RouterService from '@ember/routing/router-service';

const ALREADY_REGISTERED = 409;

interface PreregisterErrorContext {
  email: string;
  intl: IntlService;
  notifications: NotificationsService;
  router: RouterService;
}

/**
 * Handles a failed `POST /preregister` for every registration form.
 *
 * A 409 means the email already has a login, so there is nothing left to
 * collect: the visitor is sent to `login` with their email carried over as a
 * query param and told why they moved. Any other status keeps the plain error
 * toast and leaves them on the form, falling back to generic copy when the body
 * is not JSON.
 *
 * @param response the non-2xx fetch response; its body is read on the non-409 path
 * @param context the submitted email plus the services used to notify and route
 * @returns resolves once the toast is queued and any transition has settled
 */
export default async function handlePreregisterError(
  response: Response,
  { email, intl, notifications, router }: PreregisterErrorContext
): Promise<void> {
  if (response.status === ALREADY_REGISTERED) {
    notifications.add(intl.t('login.already_registered'), { appearance: 'info' });
    await router.transitionTo('login', { queryParams: { email } });
    return;
  }

  const body = await response.json().catch(() => ({}));
  notifications.add(body.error || 'An Error Occurred', { appearance: 'error' });
}
